/**
 * Fallback UI rendered by Sentry's <ErrorBoundary> when an unhandled
 * React error reaches the App root.
 *
 * Bilingual EN+AR per commandment 74. The "Reload" button is the
 * primary action; "Report this" opens a free-form Sentry user-
 * feedback dialog so the affected user can attach a note (what they
 * were doing when it broke). Both work even when the rest of the app
 * tree is unrenderable — no router or i18n provider dependency
 * inside the boundary.
 */

import { useTranslation } from "react-i18next";
import * as Sentry from "@sentry/react";

export function AppCrashScreen({
  eventId,
  resetError,
}: {
  /** Sentry event id — passed to the feedback dialog. */
  eventId?: string;
  /** Re-mount the boundary's children (Sentry's API). */
  resetError?: () => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      role="alert"
      aria-live="assertive"
      className="min-h-screen flex items-center justify-center bg-gray-50 px-4"
    >
      <div className="max-w-md w-full bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 mb-2">
          {t(
            "observability.crash.title",
            "Something went wrong",
          )}
        </h1>
        <p className="text-sm text-gray-600 mb-6">
          {t(
            "observability.crash.body",
            "An unexpected error stopped the page from rendering. Reloading usually fixes it. If the problem keeps happening, send us a quick note so we can look into it.",
          )}
        </p>
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => {
              if (resetError) resetError();
              window.location.reload();
            }}
            className="bg-gray-900 text-white text-sm font-medium px-4 py-2 rounded-md hover:bg-gray-800"
          >
            {t("observability.crash.reload", "Reload")}
          </button>
          <button
            type="button"
            onClick={() => {
              if (eventId) {
                Sentry.showReportDialog({ eventId });
              }
            }}
            disabled={!eventId}
            className="text-sm text-gray-700 underline-offset-2 hover:underline disabled:text-gray-400 disabled:no-underline"
          >
            {t("observability.crash.report", "Report this")}
          </button>
        </div>
      </div>
    </div>
  );
}
